import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import { useListMaintenanceRequestsQuery } from "../../../masters/vehicles/vehiclesApi";
import { useListLposQuery } from "../../stockApi";
import { paths } from "../../../../routes/paths";

// The requested cross-module picker: Vehicle Maintenance "Spares Needed"
// requests, once approved, sit waiting for someone to actually raise the
// LPO for them - there was no screen anywhere that surfaced this before.
// A request counts as "pending" if it has spare items and no LPO already
// exists referencing it in a non-REJECTED/non-CANCELLED status (so a
// request whose earlier LPO attempt was rejected or cancelled can be
// converted again).
const PendingVehicleRequests = () => {
  const navigate = useNavigate();
  const { data: requestsPage, isLoading: isLoadingRequests } = useListMaintenanceRequestsQuery({
    status: "APPROVED",
    pageSize: 200,
  });
  const { data: lpoPage, isLoading: isLoadingLpos } = useListLposQuery({
    purposeType: "VEHICLE_MAINTENANCE",
    pageSize: 200,
  });

  const activelyLinkedRequestGuids = useMemo(() => {
    const set = new Set();
    (lpoPage?.items || []).forEach((lpo) => {
      if (lpo.vehicleRequestRecordGuid && lpo.status !== "REJECTED" && lpo.status !== "CANCELLED") {
        set.add(lpo.vehicleRequestRecordGuid);
      }
    });
    return set;
  }, [lpoPage]);

  // MaintenanceRequestRecord (list shape) doesn't carry spareItems - only
  // MaintenanceRequestDetailRecord does. So this list shows every APPROVED,
  // not-yet-linked request (a cash-only request converted here would just
  // land on an LPO form with an empty line-items table, harmless) - the
  // actual spare items get fetched once, on demand, when LpoForm loads for
  // the specific request being converted.
  const pendingRows = (requestsPage?.items || []).filter(
    (r) => !activelyLinkedRequestGuids.has(r.recordGuid)
  );

  const handleConvert = (request) => {
    navigate(paths.stockLpoNew, { state: { fromVehicleRequest: { recordGuid: request.recordGuid, requestNumber: request.requestNumber } } });
  };

  const columns = [
    { title: "Request #", dataIndex: "requestNumber" },
    { title: "Vehicle", dataIndex: "regNo", render: (v, r) => `${v} (${r.brand} ${r.model})` },
    { title: "Requested By", dataIndex: "requestedByName" },
    { title: "Date", dataIndex: "requestDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { title: "Spare Supplier", dataIndex: "spareSupplierName", render: (v) => v || "-" },
    { title: "Branch", dataIndex: "branchCode" },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <button
          type="button"
          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
          onClick={() => handleConvert(record)}
        >
          <FileText size={14} /> Convert to LPO
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to LPOs
              </Link>
              <h4>Pending Vehicle Requests</h4>
              <h6>Approved maintenance requests waiting to be turned into an LPO</h6>
            </div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={pendingRows}
                rowKey="recordGuid"
                loading={isLoadingRequests || isLoadingLpos}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingVehicleRequests;
